import { useEffect, useRef, useState } from "react";
import jsQR from "jsqr";
import { X, ScanLine } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

/**
 * Reads the meter's QR code via the browser camera (getUserMedia + jsQR
 * polling a hidden canvas each frame) — the same JSON payload shape the
 * mobile app's scanner understands ({ serial, ... }), or a bare serial
 * string. This is now the only place a QR code can be scanned to register
 * a device — the mobile app can scan a code too, but only to *connect* to
 * a device already registered here, never to create one.
 */
export default function QrScanDialog({ open, onOpenChange, onDetected }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError("");

    navigator.mediaDevices
      ?.getUserMedia({ video: { facingMode: "environment" } })
      .then((stream) => {
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
        const tick = () => {
          const video = videoRef.current;
          const canvas = canvasRef.current;
          if (video && canvas && video.readyState === video.HAVE_ENOUGH_DATA) {
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const frame = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const code = jsQR(frame.data, frame.width, frame.height);
            if (code?.data) {
              onDetected(code.data);
              return;
            }
          }
          rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
      })
      .catch(() => setError("Camera access was denied or is unavailable. Use manual entry instead."));

    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-none border-kl-black p-0 gap-0 bg-kl-black text-white" data-testid="qr-scan-dialog">
        <DialogHeader className="p-6 pb-0">
          <p className="kl-label-dark">Camera scan</p>
          <DialogTitle className="font-display font-light text-2xl text-white">Scan meter QR code</DialogTitle>
          <DialogDescription className="text-kl-muted">Align the code printed on the device within the frame.</DialogDescription>
        </DialogHeader>
        <div className="p-6">
          {error ? (
            <div className="aspect-square flex flex-col items-center justify-center gap-3 border border-kl-line bg-kl-surface text-center px-6">
              <X size={28} className="text-kl-inactive" />
              <p className="text-sm text-kl-muted">{error}</p>
            </div>
          ) : (
            <div className="relative aspect-square overflow-hidden border border-kl-line bg-black">
              <video ref={videoRef} muted playsInline className="absolute inset-0 w-full h-full object-cover" />
              <canvas ref={canvasRef} className="hidden" />
              <div className="absolute inset-8 border-2 border-kl-green/70 pointer-events-none" />
              <ScanLine size={20} className="absolute top-3 right-3 text-kl-green animate-pulse" />
            </div>
          )}
          <button onClick={() => onOpenChange(false)} className="kl-btn-outline-dark w-full h-10 mt-5" data-testid="qr-scan-cancel">Cancel</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
